import { CONFIG } from '../../config';
import { Boss } from '../Boss';
import { Bullet } from '../Bullet';

/**
 * 보스 B - 추적자
 * 플레이어를 천천히 추적하며 주변에 원형 탄막 생성
 */
export class Chaser extends Boss {
    private fireTimer: number = 0;
    private fireInterval: number;
    private bulletCount: number;
    private moveSpeed: number;
    private minDistance: number;
    private hasEntered: boolean = false;
    private enterSpeed: number = 200;

    constructor(canvasWidth: number, canvasHeight: number, isLandscape: boolean = false) {
        // 화면 밖에서 시작 (세로: 상단, 가로: 우측)
        const startX = isLandscape ? canvasWidth + 50 : canvasWidth / 2;
        const startY = isLandscape ? canvasHeight / 2 : -50;
        const health = CONFIG.BOSS.CHASER.HEALTH;

        super(startX, startY, health, 'chaser', canvasWidth, canvasHeight);

        this.radius = 35;
        this.moveSpeed = CONFIG.BOSS.CHASER.MOVE_SPEED;
        this.fireInterval = CONFIG.BOSS.CHASER.FIRE_INTERVAL;
        this.bulletCount = CONFIG.BOSS.CHASER.BULLET_COUNT;
        this.minDistance = CONFIG.BOSS.MIN_DISTANCE;
    }

    update(deltaTime: number, playerX: number, playerY: number): void {
        if (!this.isAlive) return;

        // 입장 체크 (화면 안에 들어왔는지)
        if (!this.hasEntered) {
            const margin = this.radius + 20;
            if (this.x > margin && this.x < this.canvasWidth - margin &&
                this.y > margin && this.y < this.canvasHeight - margin) {
                this.hasEntered = true;
            }
        }

        // 플레이어 방향 계산
        const dx = playerX - this.x;
        const dy = playerY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // 입장 전에는 빠르게 이동, 입장 후에는 추적
        const currentSpeed = this.hasEntered ? this.moveSpeed : this.enterSpeed;

        if (!this.hasEntered || distance > this.minDistance) {
            const moveX = (dx / distance) * currentSpeed * deltaTime;
            const moveY = (dy / distance) * currentSpeed * deltaTime;
            this.x += moveX;
            this.y += moveY;
        }

        // 플레이어 방향으로 회전
        this.rotation = Math.atan2(dy, dx);

        // 입장 후에만 화면 경계 제한
        if (this.hasEntered) {
            this.x = Math.max(this.radius, Math.min(this.canvasWidth - this.radius, this.x));
            this.y = Math.max(this.radius, Math.min(this.canvasHeight - this.radius, this.y));
        }

        // 입장 후에만 총알 발사
        if (this.hasEntered) {
            this.fireTimer += deltaTime;
            if (this.fireTimer >= this.fireInterval) {
                this.fireTimer = 0;
                this.fireBullets(playerX, playerY);
            }
        }
    }

    private fireBullets(playerX: number, playerY: number): void {
        const bulletSpeed = 150; // 100에서 150으로 증가

        // 플레이어 방향으로 2발만 발사
        const dx = playerX - this.x;
        const dy = playerY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > 0) {
            const baseAngle = Math.atan2(dy, dx);
            const spreadAngle = Math.PI / 12; // 15도 간격

            for (let i = 0; i < 2; i++) {
                const angle = baseAngle + (i - 0.5) * spreadAngle;
                const vx = Math.cos(angle) * bulletSpeed;
                const vy = Math.sin(angle) * bulletSpeed;

                const bullet = new Bullet(this.x, this.y, vx, vy, '#FFFFFF');
                bullet.hasEnteredPlayArea = true;
                bullet.noBounce = true;
                this.bulletBuffer.push(bullet);
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        if (!this.isAlive) return;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;

        // 이등변 삼각형 (앞쪽이 뾰족한 형태)
        const tipX = this.radius * 1.2;  // 앞쪽 꼭지점
        const backX = -this.radius * 0.8; // 뒤쪽
        const halfHeight = this.radius * 0.8; // 삼각형 높이의 절반

        ctx.beginPath();
        ctx.moveTo(tipX, 0);  // 앞쪽 꼭지점
        ctx.lineTo(backX, -halfHeight);  // 왼쪽 위
        ctx.lineTo(backX, halfHeight);   // 왼쪽 아래
        ctx.closePath();
        ctx.stroke();

        // 내부 중심선 (방향 표시)
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(backX * 0.3, 0);
        ctx.lineTo(tipX * 0.5, 0);
        ctx.stroke();

        // 체력 표시 (내부 작은 삼각형)
        const healthRatio = this.health / this.maxHealth;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        const scale = 0.4 * healthRatio;
        ctx.beginPath();
        ctx.moveTo(tipX * scale, 0);
        ctx.lineTo(backX * scale, -halfHeight * scale);
        ctx.lineTo(backX * scale, halfHeight * scale);
        ctx.closePath();
        ctx.stroke();

        ctx.restore();
    }

    getName(): string {
        return 'CHASER';
    }
}
